import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { rpc } from '../lib/rpc';

export interface Branding {
  appName: string;
  companyName: string;
  logo: string | null;
}

/** Company name and logo: public in production (sign-in page), from the demo database in the demo. */
async function fetchBranding(): Promise<Branding> {
  if (__DEMO__) return rpc('branding.get', {});
  const res = await fetch('/api/branding', { credentials: 'same-origin' });
  const body = (await res.json()) as { ok: boolean; data?: Branding };
  return body.data ?? { appName: 'Projektzeit', companyName: '', logo: null };
}

export function useBranding() {
  return useQuery({ queryKey: ['branding.get'], queryFn: fetchBranding, staleTime: 5 * 60_000 });
}

export function AppMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={clsx('shrink-0', className)} aria-hidden>
      <rect width="32" height="32" rx="6" fill="var(--accent)" />
      <path d="M9 8h9a6 6 0 0 1 0 12h-5v5H9z M13 12v4h5a2 2 0 0 0 0-4z" fill="var(--accent-ink)" />
    </svg>
  );
}

/** Logo on a light plate (stays readable in dark mode), app name and company name. */
export function Brand({ subtitle, large }: { subtitle?: string; large?: boolean }) {
  const b = useBranding().data;
  const appName = b?.appName || 'Projektzeit';
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {b?.logo && (
        <span className="inline-flex max-w-full self-start rounded-md bg-white px-2 py-1.5 ring-1 ring-line">
          <img src={b.logo} alt={b.companyName || appName} className={clsx('w-auto max-w-full object-contain', large ? 'max-h-14' : 'max-h-8')} />
        </span>
      )}
      <div className="flex min-w-0 items-center gap-2">
        {!b?.logo && <AppMark className={large ? 'size-9' : 'size-7'} />}
        <div className="min-w-0">
          <div className={clsx('truncate font-semibold', large ? 'text-lg' : 'text-[15px]')}>{appName}</div>
          {(b?.companyName || subtitle) && <div className="truncate text-xs text-graphite">{[b?.companyName, subtitle].filter(Boolean).join(' · ')}</div>}
        </div>
      </div>
    </div>
  );
}
